import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import { useBodyNoScroll } from '@src/device/hooks/useBodyNoScroll'
import {
  Filter,
  FilterKeys,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'

import Button, { BUTTON_SIZE, BUTTON_TYPES } from '../../button'
import Modal from '../../modal'
import Select from '../../select'
import {
  ROW_SELECTOR_IDS,
  SORT_BY,
  SORT_OPTIONS_CLP_EXPERIMENT,
  SortByKeys,
  B2B_SELF_FUNDED_SORT_OPTIONS,
} from '../constants/constants'
import Cross from '../icons/cross.inline.svg'
import { getDisplayNameForFilterValue } from '../menu/filterUtils'

import styles from './filterModal.module.scss'

export interface FilterModalProps {
  open: boolean
  onCancel: () => void
  className?: string
  filters: Filter[]
  noOfItems: number
  selectedFilters: SelectedFilter
  updateQuery: (params: UpdateQueryParams) => void
  showSelectedOptionOnDropDown?: boolean
  skipSorting?: boolean
  sortBy?: string
  isB2BMode?: boolean
}

const FilterModal = ({ filters = [], open = false, ...props }: FilterModalProps) => {
  const { t } = useTranslation('common')
  const classes = classnames(styles.modal, props.className)
  useBodyNoScroll(open)

  return (
    <Modal
      open={open}
      className={classes}
      showCloseButton
      onClose={props.onCancel}
      // @i18n-ignore
      crossAlignment="left"
      closeButtonClassName={styles.modal__close}
    >
      <div className={styles.modal__header}>
        <div className={styles.modal__header__text}>
          {props.noOfItems} {t('items')}
        </div>
      </div>
      <div>
        <div className={styles.modal__content}>
          {filters
            .filter(x => x.values.length > 0)
            .map(filter => {
              return (
                <Select
                  id={filter.name}
                  key={filter.config.label}
                  className={styles.modal__content__filter}
                  placeholder={filter.config.label}
                  options={filter.values.map(x => Filter.makeSelectFilterOptionFromValue(x))}
                  showMultipleColumns={ROW_SELECTOR_IDS.includes(filter.name)}
                  OnOptionClick={(_e, optionValue) => {
                    props.updateQuery({
                      filter: {
                        // @i18n-ignore
                        type: 'toggle',
                        value: optionValue,
                        name: filter.name,
                        label: filter.config.label,
                      },
                    })
                  }}
                  selectedOptions={SelectedFilter.getSelectedFilterValuesByFilterName(
                    props.selectedFilters,
                    filter.name as keyof SelectedFilter,
                  )}
                />
              )
            })}
          {!props.skipSorting ? (
            <Select
              id="Sort"
              placeholder={t('sort')}
              options={props?.isB2BMode ? B2B_SELF_FUNDED_SORT_OPTIONS : SORT_OPTIONS_CLP_EXPERIMENT}
              OnOptionClick={(_e, value) => {
                props.updateQuery({
                  sort: {
                    // @i18n-ignore
                    type: 'toggle',
                    value,
                  },
                })
                /** NOTE: If this causes issues - check with BE team if they still need Sort key in filters otherwise clear this code */
                // props.onSortClick(value)
                // const currentSortFiltersFromState: string[] = SelectedFilter.getSelectedFilterValuesByFilterName(
                //   props.selectedFilters,
                //   'sort',
                // )
                // const selectedSortOption = SORT_OPTIONS.find(option => option.value === value)
                // const { label: selectedSortOptionLabel } = selectedSortOption || { label: '' }

                // if (currentSortFiltersFromState.includes(selectedSortOptionLabel)) {
                //   props.onSelectedFiltersChange(
                //     SelectedFilter.removeValueFromSelectedFilter(props.selectedFilters, selectedSortOptionLabel),
                //   )
                // } else {
                //   props.onSelectedFiltersChange(
                //     SelectedFilter.addValueToSelectedFilter(props.selectedFilters, 'sort', selectedSortOptionLabel),
                //   )
                // }
              }}
              className={styles.modal__content__filter}
              showSelectedOptionOnDropDown={props.showSelectedOptionOnDropDown}
            />
          ) : null}
        </div>
        {!!(
          SelectedFilter.getSelectedValuesAsList(props.selectedFilters).length ||
          props.sortBy !== SORT_BY.FEATURED.value
        ) && (
          <div className={styles.modal__content__filterValues}>
            {Object.entries(props.selectedFilters).flatMap(([filterName, filterValues]) => {
              if (!filterValues) return []
              return filterValues.map((filterValueItem: FilterKeys) => (
                <div key={`${filterName}-${filterValueItem}`} className={styles.modal__content__filterValues__value}>
                  <div className={styles.modal__content__filterValues__value__text}>
                    {getDisplayNameForFilterValue({ filters, filterName, filterValue: filterValueItem })}
                  </div>
                  <Cross
                    width={8}
                    onClick={() => {
                      props.updateQuery({
                        filter: {
                          // @i18n-ignore
                          type: 'remove',
                          name: filterName,
                          value: filterValueItem,
                        },
                      })
                    }}
                  />
                </div>
              ))
            })}
            {props.sortBy && props.sortBy !== SORT_BY.FEATURED.value && Object.hasOwn(SORT_BY, props.sortBy) && (
              <div key={`sort-${props.sortBy}`} className={styles.modal__content__filterValues__value}>
                <div className={styles.modal__content__filterValues__value__text}>
                  {t('sort_var1', { var1: t(SORT_BY[props.sortBy as SortByKeys]?.labelKey) })}
                </div>
                <Cross
                  width={8}
                  onClick={() => {
                    props.updateQuery({
                      sort: {
                        // @i18n-ignore
                        type: 'reset',
                      },
                    })
                  }}
                />
              </div>
            )}
          </div>
        )}
        <Button
          className={styles.modal__content__button}
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZE.MEDIUM}
          disabled={false}
          round={true}
          onClick={() => {
            props.onCancel()
          }}
        >
          {t('show_me_the_goods')}
        </Button>
        {(SelectedFilter.getSelectedValuesAsList(props.selectedFilters).length > 0 ||
          (props.sortBy && props.sortBy !== SORT_BY.FEATURED.value)) && (
          <div
            onClick={() => {
              props.updateQuery({
                filter: {
                  // @i18n-ignore
                  type: 'reset',
                },
                sort: {
                  // @i18n-ignore
                  type: 'reset',
                },
              })
            }}
            className={styles.modal__content__clear}
          >
            <span className={styles.modal__content__clear__text}>{t('clear_all_filters')}</span>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default FilterModal
